import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { CKEditor } from "ckeditor4-react";
import s from "../modules/TrainerEdit.module.css";
import { useT, useI18nReady } from "../components/TextProvider";

/**
 * Страница: Редактировать тренажёр
 * GET   https://apitr.subxonqulov.uz/api/vazifalar/{id}/
 * PATCH https://apitr.subxonqulov.uz/api/vazifalar/{id}/
 * Поля: name, savol, img, tushuncha_b2 (HTML), promt, qisqa_tavsif, user (id), AI (id)
 */

const VAZIFALAR_ENDPOINT =
  process.env.REACT_APP_VAZIFALAR_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/vazifalar/";

const AIKEYS_ENDPOINT =
  process.env.REACT_APP_AIKEYS_ENDPOINT ||
  "https://apitr.subxonqulov.uz/api/aikeys/";

// ===== helpers: auth
function getStoredUser() {
  const ls = window.localStorage.getItem("auth:user");
  const ss = window.sessionStorage.getItem("auth:user");
  const raw = ls ?? ss;
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}
function getAnyTokenFromStorage() {
  const pick = (s) =>
    s.getItem("auth:access") || s.getItem("auth:token") || s.getItem("auth:key") ||
    s.getItem("auth:jwt") || s.getItem("auth:auth_token") || null;
  return pick(window.localStorage) || pick(window.sessionStorage);
}
function makeAuthHeader(tokenLike) {
  if (!tokenLike) return {};
  const token = String(tokenLike);
  const forcedScheme = process.env.REACT_APP_AUTH_SCHEME; // "Bearer" | "Token" | "JWT"
  if (forcedScheme) return { Authorization: `${forcedScheme} ${token}` };
  const looksLikeJwt = token.startsWith("eyJ");
  return looksLikeJwt ? { Authorization: `Bearer ${token}` } : { Authorization: `Token ${token}` };
}

export default function TrainerEdit() {
  const t = useT();
  const ready = useI18nReady();

  const navigate = useNavigate();
  const { id } = useParams();
  const user = useMemo(() => getStoredUser(), []);

  const [ais, setAis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [form, setForm] = useState({
    name: "",
    savol: "",
    promt: "",
    qisqa_tavsif: "",
    AI: "",
  });
  const [tushunchaHTML, setTushunchaHTML] = useState("");
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [existingImg, setExistingImg] = useState("");

  // загрузка AI
  useEffect(() => {
    let abort = false;
    (async () => {
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(AIKEYS_ENDPOINT, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        const data = await res.json().catch(() => []);
        if (!abort) setAis(Array.isArray(data) ? data : []);
      } catch {
        if (!abort) setAis([]);
      }
    })();
    return () => { abort = true; };
  }, []);

  // загрузка текущего тренажёра
  useEffect(() => {
    let abort = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = getAnyTokenFromStorage();
        const res = await fetch(`${VAZIFALAR_ENDPOINT}${id}/`, {
          method: "GET",
          headers: { "Content-Type": "application/json", ...(token ? makeAuthHeader(token) : {}) },
          credentials: "include",
        });
        const data = await res.json().catch(() => null);
        if (!res.ok) throw new Error(data?.detail || data?.message || "Mashqni yuklashda xatolik yuz berdi");
        if (abort) return;

        setForm({
          name: data?.name ?? "",
          savol: data?.savol ?? "",
          promt: data?.promt ?? "",
          qisqa_tavsif: data?.qisqa_tavsif ?? "",
          AI: data?.AI ? String(data.AI) : "",
        });
        setTushunchaHTML(data?.tushuncha_b2 || "");
        setExistingImg(data?.img || "");
        setImgPreview(data?.img || "");
      } catch (e) {
        if (!abort) setError(e?.message || "Yuklashda xatolik");
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [id]);

  function onChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function onImgChange(e) {
    const file = e.target.files?.[0];
    if (file) {
      setImgFile(file);
      const url = URL.createObjectURL(file);
      setImgPreview(url);
    }
  }

  async function handleSave(e) {
    e.preventDefault();
    setError(""); setSuccess("");

    if (!form.name.trim()) { setError("Mashqning nomini kiriting"); return; }
    if (!form.savol.trim()) { setError("“savol” maydonini toʻldiring"); return; }
    if (!form.AI) { setError("AI ni tanlang"); return; }

    try {
      setSaving(true);
      const token = getAnyTokenFromStorage();
      const fd = new FormData();

      // PATCH — отправляем только те поля, которые хотим обновить
      fd.append("name", form.name.trim());
      fd.append("savol", form.savol.trim());
      fd.append("tushuncha_b2", tushunchaHTML || "");
      fd.append("promt", form.promt.trim());
      fd.append("qisqa_tavsif", form.qisqa_tavsif.trim());
      fd.append("AI", String(form.AI));
      // user умышленно НЕ меняем; если нужно — раскомментируйте:
      // if (user?.id) fd.append("user", String(user.id));

      if (imgFile) {
        fd.append("img", imgFile);
      }

      const res = await fetch(`${VAZIFALAR_ENDPOINT}${id}/`, {
        method: "PATCH",
        headers: { ...(token ? makeAuthHeader(token) : {}) },
        body: fd,
        credentials: "include",
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const serverMsg = (typeof data === "string" && data) || data?.detail || data?.error || data?.message ||
          (data && Array.isArray(Object.values(data)[0]) ? Object.values(data)[0] : null) || "Saqlab bo'lmadi";
        throw new Error(Array.isArray(serverMsg) ? serverMsg.join(" ") : String(serverMsg));
      }

      setSuccess("O'zgarishlar saqlandi");
      // обновим локальное состояние картинок, если сервер вернул новый url
      if (data?.img) {
        setExistingImg(data.img);
        setImgPreview(data.img);
        setImgFile(null);
      }
      // По желанию — вернуться к списку
      // setTimeout(() => navigate("/trainer/my", { replace: true }), 500);
    } catch (e) {
      setError(e?.message || "Saqlashda xatolik yuz berdi");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm("Trenajorni o'chirmoqchisiz? Bu amal qaytarilib bo'lmaydi.")) return;
    try {
      const token = getAnyTokenFromStorage();
      const res = await fetch(`${VAZIFALAR_ENDPOINT}${id}/`, {
        method: "DELETE",
        headers: { ...(token ? makeAuthHeader(token) : {}) },
        credentials: "include",
      });
      if (!res.ok && res.status !== 204) throw new Error("Server o'chirishni rad etdi");
      navigate("/TrainerMy", { replace: true });
    } catch (e) {
      setError(e?.message || "O'chirib bo'lmaydi");
    }
  }

  if (loading) return <div className={s.wrapper}><div className={s.info}>Yuklanmoqda…</div></div>;

  return (
    <div className={s.wrapper}>
      <div className={s.headRow}>
        <h1 className={s.title}>{t('TrainEditTitle')}</h1>
        <div className={s.actions}>
          <Link className={s.secondary} to="/TrainerMy">← {t('ruyxatgaQaytish')}</Link>
          {/* <button className={s.danger} onClick={handleDelete}>Удалить</button> */}
        </div>
      </div>

      {error && <div className={s.error}>{error}</div>}
      {success && <div className={s.success}>{success}</div>}

      <form className={s.form} onSubmit={handleSave}>
        {/* Название */}
        <div className={s.field}>
          <label className={s.label}>{t('addTrenName')}</label>
          <input className={s.input} name="name" value={form.name} onChange={onChange} required />
        </div>

        {/* Вопрос */}
        <div className={s.field}>
          <label className={s.label}>{t('addtrenQues')}</label>
          <textarea className={s.textarea} name="savol" value={form.savol} onChange={onChange} rows={9} required />
        </div>

        {/* Изображение */}
        <div className={s.field}>
          <label className={s.label}>{t('addTrenImg')}</label>
          <input type="file" accept="image/*" onChange={onImgChange} />
          {(imgPreview || existingImg) && (
            <div className={s.previewBox}>
              <img src={imgPreview || existingImg} alt="preview" className={s.previewImg} />
            </div>
          )}
        </div>

        {/* Rich text */}
        <div className={s.field}>
          <label className={s.label}>{t('addTrenTush')}</label>
          <CKEditor
            editorUrl="https://cdn.ckeditor.com/4.22.1/full-all/ckeditor.js"
            initData={tushunchaHTML}
            onChange={(evt) => setTushunchaHTML(evt.editor.getData())}
            onInstanceReady={(evt) => { try { evt.editor.resize('100%', 460); } catch {} }}
            config={{
              language: "ru",
              contentsLangDirection: "ltr",
              contentsCss: [
                "https://cdn.ckeditor.com/4.22.1/full-all/contents.css",
                "body { direction: ltr; text-align: left; }",
              ],
              height: 260,
              toolbar: [
                { name: "document", items: ["Source","-","NewPage","Preview","Print"] },
                { name: "clipboard", items: ["Cut","Copy","Paste","PasteText","PasteFromWord","-","Undo","Redo"] },
                { name: "editing",  items: ["Find","Replace","SelectAll","Scayt"] },
                { name: "forms",    items: ["Checkbox","Radio","TextField","Textarea","Select","Button","ImageButton","HiddenField"] },
                "/",
                { name: "basicstyles", items: ["Bold","Italic","Underline","Strike","Subscript","Superscript","-","RemoveFormat"] },
                { name: "paragraph",   items: ["NumberedList","BulletedList","-","Outdent","Indent","-","Blockquote","CreateDiv","-","JustifyLeft","JustifyCenter","JustifyRight","JustifyBlock","-","BidiLtr","BidiRtl","Language"] },
                { name: "links",       items: ["Link","Unlink","Anchor"] },
                { name: "insert",      items: ["Image","Table","HorizontalRule","Smiley","SpecialChar","PageBreak","Iframe"] },
                "/",
                { name: "styles", items: ["Styles","Format","Font","FontSize"] },
                { name: "colors", items: ["TextColor","BGColor"] },
                { name: "tools",  items: ["Maximize","ShowBlocks"] },
                { name: "about",  items: ["About"] },
              ],
              extraPlugins: "colorbutton,font,justify,language,bidi,div,find,smiley,iframe",
              removePlugins: "elementspath",
              resize_enabled: true,
            }}
          />
          {/* <div className={s.hint}>HTML будет отправляться в <code>tushuncha_b2</code>.</div> */}
        </div>

        {/* promt */}
        <div className={s.field}>
          <label className={s.label}>{t('addTrenPrompt')}</label>
          <textarea className={s.textarea} name="promt" value={form.promt} onChange={onChange} rows={7} />
        </div>

        {/* qisqa_tavsif */}
        <div className={s.field}>
          <label className={s.label}>{t('addTrenDesc')}</label>
          <textarea className={s.textarea} name="qisqa_tavsif" value={form.qisqa_tavsif} onChange={onChange} rows={7} />
        </div>

        {/* AI выбор */}
        <div className={s.field}>
          <label className={s.label}>{t('addTrenAI')}</label>
          <select className={s.select} name="AI" value={form.AI} onChange={onChange} required>
            <option value="" disabled>{t('addTrenAIPl')}…</option>
            {ais.map((ai) => (
              <option key={ai.id} value={ai.id}>{ai.name}</option>
            ))}
          </select>
        </div>

        <div className={s.actions}>
          <button className={s.submit} type="submit" disabled={saving}>{saving ? "Saqlanmoqda…" : "Saqlash"}</button>
          <button type="button" className={s.secondary} onClick={() => navigate(-1)} disabled={saving}>{t('bekorQilish')}</button>
        </div>
      </form>
    </div>
  );
}




